import { Box, Progress, Text, HStack } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { ModelStatusResponse } from 'src/api/models';
import { formatTimeRemaining } from './utils';

type ModelProgressBarProps = {
  label: string;
  status: ModelStatusResponse | null;
};

export const ModelProgressBar = ({ label, status }: ModelProgressBarProps) => {
  const isRunning = status?.status === 'running';
  const isSuccess = status?.status === 'success';
  const isFailed = status?.status === 'fail';
  const progress = status?.progress ?? 0;
  const timeRemaining = formatTimeRemaining(
    status?.estimatedTimeRemaining ?? null,
  );

  return (
    <Box w="100%">
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Text fontSize="14px" fontWeight="600" color="text.title">
            {label}
          </Text>
          {isSuccess && <Icon iconName="check" color="green.500" boxSize={4} />}
          {isFailed && <Icon iconName="warning" color="red.500" boxSize={4} />}
        </HStack>
        <Text fontSize="13px" color="text.muted">
          {!status && 'Waiting...'}
          {isRunning && `${progress}%`}
          {isSuccess && 'Complete'}
          {isFailed && 'Failed'}
        </Text>
      </HStack>

      <Progress
        value={progress}
        isIndeterminate={isRunning && progress === 0}
        size="lg"
        borderRadius="full"
        bg="surface.card"
        colorScheme={isFailed ? 'red' : 'green'}
        h="12px"
        sx={{
          '& > div': {
            borderRadius: '9999px',
            bg: isFailed ? 'red.500' : 'icon.green',
            transition: 'width 0.5s ease-in-out',
          },
        }}
      />

      {isRunning && timeRemaining && (
        <Text fontSize="12px" color="text.muted" mt={1}>
          {timeRemaining}
        </Text>
      )}

      {isFailed && status?.errorMessage && (
        <Text fontSize="12px" color="red.500" mt={1}>
          {status.errorMessage}
        </Text>
      )}
    </Box>
  );
};
